import React, { useState, useEffect } from 'react';
import { Button, Icon } from 'antd';
import api from 'api';
import Autocomplete from '../AutoComplete';

// Custom form control for the contract "Bill-To Customer" field. It renders the
// standard customer autocomplete plus a shortcut that copies the selected *site's*
// default billing customer into this field.
//
// The site's billing customer lives on sites.billing_customer_id (its display name
// comes from sites_extended.customer_name) and is returned by GET /sites/:id. It is
// NOT carried on the contract's own record, nor in the site autocomplete suggestions
// (those only fetch id,name,address and discard everything else on select), so we
// fetch the site whenever the chosen site changes to learn its bill-to customer.
//
// getFieldDecorator injects `value` (bill_to_customer_id) and `onChange` into this
// component (components/Form/index.js renderFormItemComponent). We call onChange to
// write the customer id, and keep the display label in local state so the AutoComplete
// shows the customer name after a programmatic set -- its label otherwise only resolves
// from a live search or the originally-loaded record, neither of which covers the
// button-driven set.
function BillToCustomer({ value, onChange, item, values, form }) {
    const siteFieldKey = item.siteFieldKey || 'site_id';

    const initialLabel = values
        ? (item.displayFunc ? item.displayFunc(values) : values[item.displayKey])
        : null;

    const [label, setLabel] = useState(initialLabel);
    // {id, name} of the selected site's default billing customer, or null if the site
    // has none (or no site is selected). Drives whether the shortcut button appears.
    const [siteBillTo, setSiteBillTo] = useState(null);

    // Resolve the display label when the field value was set programmatically. The
    // "New Contract for Site" action pushes bill_to_customer_id via pushedFormValues,
    // so the create form opens with the id already set but no label (values is null on
    // create), rendering a blank/"null" chip even though the value submits correctly.
    // When we have a value but no live label, fetch the customer to learn its name --
    // same approach as SiteAutocomplete.
    useEffect(() => {
        if (!value || label) return;

        let cancelled = false;
        api.get('/customers/' + value)
            .then(resp => {
                if (cancelled) return;
                const customer = (resp && resp.body) || {};
                if (customer.name) setLabel(customer.name);
            })
            .catch(() => {});

        return () => { cancelled = true; };
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    // Live-selected site on the contract form (falls back to the loaded record).
    const siteId = form ? form.getFieldValue(siteFieldKey) : (values ? values[siteFieldKey] : null);

    useEffect(() => {
        if (!siteId) {
            setSiteBillTo(null);
            return;
        }

        let cancelled = false;
        api.get('/sites/' + siteId)
            .then(resp => {
                if (cancelled) return;
                const site = (resp && resp.body) || {};
                if (site.billing_customer_id) {
                    setSiteBillTo({ id: site.billing_customer_id, name: site.customer_name });
                } else {
                    setSiteBillTo(null);
                }
            })
            .catch(() => { if (!cancelled) setSiteBillTo(null); });

        return () => { cancelled = true; };
    }, [siteId]);

    const applySiteBillTo = () => {
        if (!siteBillTo) return;
        onChange(siteBillTo.id);   // writes the id into the antd field store
        setLabel(siteBillTo.name); // so the autocomplete renders the customer name
    };

    const alreadySet = siteBillTo && value === siteBillTo.id;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Autocomplete
                value={value}
                onChange={onChange}
                valueLabel={label}
                suggestFormat={item.suggestFormat}
                suggestFields={item.suggestFields}
                suggestUrl={item.suggestUrl}
                placeholder={item.placeholder} />
            {siteBillTo ? (
                <Button
                    type="link"
                    size="small"
                    disabled={alreadySet}
                    onClick={applySiteBillTo}
                    style={{ alignSelf: 'flex-start', padding: '4px 0', height: 'auto' }}>
                    <Icon type="copy" />
                    {alreadySet
                        ? ` Using site's bill-to customer (${siteBillTo.name})`
                        : ` Use site's bill-to customer (${siteBillTo.name})`}
                </Button>
            ) : null}
        </div>
    );
}

export default BillToCustomer;
